import React, { useEffect, useRef, useState } from "react";
import { C, font, mono } from "../lib/styles.js";
import { LogoMarkSmall } from "./Logo.jsx";

export default function AppTopbar({
  appName,
  navItems,
  userInitials,
  userEmail,
  userName,
  onGoHome,
  onSignOut,
  activeNav: controlledNav,
  onNavChange,
  onSearch,
  onFeedback,
  showWho = false,
}) {
  const [internalNav, setInternalNav] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navMenuRef = useRef(null);
  const activeNav = controlledNav !== undefined ? controlledNav : internalNav;
  // An app whose one tab has the app's own name (Ingest) would show it twice
  // at desktop widths, in the breadcrumb and again as a lone tab beside it;
  // the breadcrumb stays and the lone tab goes. The phone menu keeps it.
  // Whose account, as the rig console says it (RigV2App displayNameOf): the
  // name if it has any letters, else the email.
  const whoName = (typeof userName === "string" && userName.trim()) || (typeof userEmail === "string" ? userEmail : "");
  const soleNavIsApp = Array.isArray(navItems) && navItems.length === 1 && navItems[0] === appName;
  const handleNav = (i) => {
    if (onNavChange) onNavChange(i);
    else setInternalNav(i);
  };

  useEffect(() => {
    if (!menuOpen && !navMenuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (navMenuRef.current && !navMenuRef.current.contains(e.target)) setNavMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") { setMenuOpen(false); setNavMenuOpen(false); } };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, navMenuOpen]);

  return (
    <div className="topbar-bar" style={styles.bar}>
      {/* Home button */}
      <a
        className="topbar-home-btn"
        onClick={onGoHome}
        style={styles.homeBtn}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = C.surfaceAlt;
          e.currentTarget.style.borderColor = "#D0CDC8";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = C.bg;
          e.currentTarget.style.borderColor = C.border;
        }}
      >
        <LogoMarkSmall size={18} />
        <span className="topbar-home-text" style={styles.homeBtnText}>NYC Suite</span>
        {/* Breadcrumb (approved mockup topbar-appswitch-v1 variant C, 2026-08-28):
            the app name lives inside the home button — NYC Suite / Jobs, one
            click target — instead of a dead standalone label that collided with
            the nav tab of the same name. On phones the "NYC Suite" text and the
            slash are hidden, so the button reads [glyph] AppName, same footprint
            as the old label but now tappable. */}
        {appName && <span aria-hidden="true" className="topbar-crumb-sep" style={styles.crumbSep}>/</span>}
        {appName && <span className="topbar-crumb-app" style={styles.crumbApp}>{appName}</span>}
      </a>

      {/* Mobile nav menu — hidden on desktop (inline display:none), shown <=767px
          via responsive.css (.topbar-nav-mobile) where the desktop .topbar-nav is hidden. */}
      <div ref={navMenuRef} className="topbar-nav-mobile" style={styles.navMobileWrap}>
        <button
          type="button"
          style={styles.navMobileBtn}
          onClick={() => { setMenuOpen(false); setNavMenuOpen((v) => !v); }}
          aria-haspopup="true"
          aria-expanded={navMenuOpen}
        >
          <span style={styles.navMobileLabel}>{(navItems || [])[activeNav] || "Menu"}</span>
          <span style={styles.navMobileChevron}>▾</span>
        </button>
        {navMenuOpen && (
          <div style={styles.navMobileMenu} role="menu">
            {(navItems || []).map((item, i) => (
              <button
                key={item}
                type="button"
                role="menuitem"
                style={{ ...styles.navMobileItem, ...(i === activeNav ? styles.navMobileItemActive : {}) }}
                onClick={() => { handleNav(i); setNavMenuOpen(false); }}
              >
                {item}
              </button>
            ))}
            {/* The Feedback tab's phone entry (src/components/FeedbackTab.jsx):
                on phones nothing floats over the page, so it lives here. */}
            {onFeedback && (
              <button
                type="button"
                role="menuitem"
                className="topbar-nav-feedback"
                style={{ ...styles.navMobileItem, ...styles.navMobileFeedback }}
                onClick={() => { setNavMenuOpen(false); onFeedback(); }}
              >
                Send feedback
              </button>
            )}
            {/* Shared phones (docs/ops/password-signin.md): whose account this
                is, then Sign out, at the end of this menu below a line. */}
            {whoName && (
              <div className="topbar-nav-who" style={styles.navMobileWho}>Signed in as <strong>{whoName}</strong></div>
            )}
            {onSignOut && (
              <button
                type="button"
                role="menuitem"
                className="topbar-nav-signout"
                style={{ ...styles.navMobileItem, ...styles.navMobileSignOut }}
                onClick={() => { setNavMenuOpen(false); onSignOut(); }}
              >
                Sign out
              </button>
            )}
          </div>
        )}
      </div>

      {/* Nav items (desktop) */}
      <div className="topbar-nav" style={styles.nav}>
        {(soleNavIsApp ? [] : navItems || []).map((item, i) => (
          <div
            key={item}
            style={{
              ...styles.navItem,
              ...(i === activeNav ? styles.navItemActive : {}),
            }}
            onClick={() => handleNav(i)}
            onMouseEnter={(e) => {
              if (i !== activeNav) {
                e.currentTarget.style.color = C.text;
                e.currentTarget.style.background = C.bg;
              }
            }}
            onMouseLeave={(e) => {
              if (i !== activeNav) {
                e.currentTarget.style.color = C.muted;
                e.currentTarget.style.background = "transparent";
              }
            }}
          >
            {item}
          </div>
        ))}
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* ⌘K badge — only for apps that pass onSearch (today: CRM). Every other
          app's bar is unchanged, which is why this is an optional prop rather
          than something keyed off appName. */}
      {onSearch && (
        <button
          type="button"
          className="topbar-search-kbd"
          style={styles.searchKbd}
          onClick={onSearch}
          aria-label="Search"
          title="Search (⌘K)"
        >
          ⌘K
        </button>
      )}

      {/* Sign out in plain sight at desktop widths (hidden below 900 px by
          responsive.css, where the account menu, and on phones the tab menu
          above, carry it). */}
      {/* Whose account this is, beside Sign out, for the apps used on shared
          devices (Ingest: docs/ops/password-signin.md). Same widths as the
          button; narrower, the app's own "Signed in as" line carries it. */}
      {showWho && onSignOut && whoName && (
        <span className="topbar-who" style={styles.who}>Signed in as <strong style={styles.whoName}>{whoName}</strong></span>
      )}
      {onSignOut && (
        <button type="button" className="topbar-signout" style={styles.signOut} onClick={onSignOut}>
          Sign out
        </button>
      )}

      {/* User avatar + menu */}
      <div ref={menuRef} style={styles.userWrap}>
        <button
          type="button"
          className="topbar-avatar"
          style={styles.userAvatar}
          onClick={() => { setNavMenuOpen(false); setMenuOpen((v) => !v); }}
          aria-label="Account menu"
          aria-expanded={menuOpen}
        >
          {userInitials}
        </button>
        {menuOpen && (
          <div className="topbar-account-menu" style={styles.menu} role="menu">
            {userEmail && <div style={styles.menuEmail}>{userEmail}</div>}
            <button
              type="button"
              style={styles.menuItem}
              onClick={() => { setMenuOpen(false); if (onSignOut) onSignOut(); }}
              role="menuitem"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  bar: {
    height: 48,
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  homeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 12px 4px 8px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.bg,
    cursor: "pointer",
    transition: "all 0.1s ease",
    textDecoration: "none",
    color: C.text,
    // Lets the crumb-app ellipsis engage when the bar is tight (phones).
    minWidth: 0,
  },
  homeBtnText: {
    fontSize: 12,
    fontWeight: 400,
    fontFamily: font,
    color: C.muted,
  },
  crumbSep: {
    fontSize: 11,
    color: C.muted,
    opacity: 0.6,
  },
  crumbApp: {
    fontSize: 12,
    fontWeight: 400,
    color: C.text,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  nav: {
    display: "flex",
    gap: 4,
    marginLeft: 16,
  },
  navItem: {
    fontSize: 12,
    fontWeight: 300,
    color: C.muted,
    padding: "6px 12px",
    borderRadius: 6,
    cursor: "pointer",
    transition: "all 0.1s ease",
  },
  navItemActive: {
    color: C.text,
    fontWeight: 400,
    background: C.bg,
  },
  navMobileWrap: {
    position: "relative",
    display: "none",
    marginLeft: 8,
  },
  navMobileBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.bg,
    color: C.text,
    fontSize: 12,
    fontWeight: 400,
    fontFamily: font,
    cursor: "pointer",
    maxWidth: 170,
  },
  navMobileLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  navMobileChevron: {
    fontSize: 9,
    color: C.muted,
    flexShrink: 0,
  },
  navMobileMenu: {
    position: "absolute",
    top: 38,
    left: 0,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    minWidth: 180,
    padding: 6,
    zIndex: 200,
    fontFamily: font,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  navMobileItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    fontSize: 13,
    color: C.text,
    fontFamily: font,
    cursor: "pointer",
  },
  navMobileItemActive: {
    background: C.surfaceAlt,
    fontWeight: 500,
  },
  navMobileWho: {
    borderTop: `1px solid ${C.border}`,
    marginTop: 4,
    padding: "10px 12px 2px",
    fontSize: 13,
    color: C.muted,
    overflowWrap: "anywhere",
  },
  navMobileSignOut: {
    minHeight: 44,
  },
  navMobileFeedback: {
    borderTop: `1px solid ${C.border}`,
    borderRadius: 0,
    marginTop: 4,
    color: C.muted,
  },
  signOut: {
    fontFamily: font,
    fontSize: 12,
    color: C.text,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: "5px 12px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  who: {
    fontFamily: font,
    fontSize: 12,
    fontWeight: 300,
    color: C.muted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  whoName: {
    fontWeight: 500,
    color: C.text,
  },
  searchKbd: {
    fontFamily: mono,
    fontSize: 10,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: "2px 6px",
    background: C.surface,
    cursor: "pointer",
  },
  userWrap: {
    position: "relative",
  },
  userAvatar: {
    width: 28,
    height: 28,
    borderRadius: 9999,
    background: C.bg,
    border: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 400,
    color: C.muted,
    cursor: "pointer",
    padding: 0,
    fontFamily: font,
  },
  menu: {
    position: "absolute",
    top: 36,
    right: 0,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    minWidth: 200,
    padding: 6,
    zIndex: 200,
    fontFamily: font,
  },
  menuEmail: {
    padding: "8px 12px 6px",
    fontSize: 11,
    color: C.muted,
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menuItem: {
    display: "block",
    width: "100%",
    minHeight: 44,
    textAlign: "left",
    padding: "8px 12px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    fontSize: 13,
    color: C.text,
    fontFamily: font,
    cursor: "pointer",
  },
};
